import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import supabase from 'lib/supabase-browser'
import { Project } from 'lib/types/generated-types'
import { useRouter } from 'next/router'
import { useAuth } from 'util/auth'
import { useTeamByOwner } from './teams'

interface ProjectQueryData {
  items: Project[]
  totalCount: number | null
}

export const useListProjects = (): UseQueryResult<ProjectQueryData, Error> => {
  const auth = useAuth()
  return useQuery<ProjectQueryData, Error>(
    ['projects'],
    async (): Promise<ProjectQueryData> => {
      try {
        const userId = auth.user ? auth.user.id : ''

        const query = supabase
          .from('projects')
          .select('*, project_members(*)', { count: 'exact' })
          .or(`owner.eq.${userId}`)
          .eq('archived', false)
          .order('created_at', { ascending: true })

        const { data, error, count } = await query

        if (error) {
          console.error('Error fetching projects:', error.message)
          throw new Error(error.message)
        }

        return { items: (data as Project[]) || [], totalCount: data.length }
      } catch (error: any) {
        console.error('Error fetching data:', error.message)
        return { items: [], totalCount: null }
      }
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  )
}

export const useTeamProjects = (): UseQueryResult<ProjectQueryData, Error> => {
  const auth = useAuth()
  return useQuery<ProjectQueryData, Error>(
    ['projects', 'member'],
    async (): Promise<ProjectQueryData> => {
      try {
        const userId = auth.user ? auth.user.id : ''

        const query = supabase
          .from('projects')
          .select('*, project_members!inner(*)')
          .neq('owner', userId)
          .eq('project_members.user_id', userId)
          .eq('archived', false)
          .order('created_at', { ascending: true })

        const { data, error, count } = await query

        if (error) {
          console.error('Error fetching team projects:', error.message)
          throw new Error(error.message)
        }

        return { items: (data as Project[]) || [], totalCount: count }
      } catch (error: any) {
        console.error('Error fetching data:', error.message)
        return { items: [], totalCount: null }
      }
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  )
}

export const useProject = ({ projectId }: { projectId?: number }) => {
  return useQuery(
    ['project', projectId],
    async () => {
      if (!projectId) return null
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .eq('archived', false)
        .single()

      if (error) {
        throw new Error(error.message)
      }

      return data
    },
    {
      enabled: !!projectId && projectId !== -1,
      keepPreviousData: true,
    }
  )
}

export const useListProjectsByTeamId = ({ teamId }: { teamId: number }) => {
  return useQuery(
    ['projects', 'team', teamId],
    async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('team_id', teamId)

      if (error) {
        throw new Error(error.message)
      }

      return data
    },
    {
      enabled: !!teamId && teamId !== -1,
    }
  )
}

export const useCreateProjectMutation = () => {
  const queryClient = useQueryClient()
  const auth = useAuth()
  const router = useRouter()
  const { data: team } = useTeamByOwner({
    owner: auth.user ? auth.user.id : '',
  })

  return useMutation(
    async ({
      projectToInsert,
      addToProject = true,
    }: {
      projectToInsert: Partial<Project>
      addToProject: boolean
    }) => {
      if (!auth.user || !team) throw new Error('User not found')
      const projectToInsertWithoutId = { ...projectToInsert }
      delete projectToInsertWithoutId.id
      const { data, error } = await supabase
        .from('projects')
        .insert([
          {
            ...projectToInsertWithoutId,
            owner: auth.user?.id,
            team_id: team.id,
          },
        ])
        .select('*')
        .single()

      if (error) {
        throw new Error(error.message)
      }

      if (addToProject) {
        const { data: projectMemberData, error: projectMemberError } =
          await supabase.from('project_members').insert([
            {
              project_id: data.id,
              user_id: auth.user?.id,
              role: 'owner',
              status: 'approved',
              email: auth.user?.email,
            },
          ])

        if (projectMemberError) {
          throw new Error(projectMemberError.message)
        }
      }

      return data
    },
    {
      onSuccess: async (data) => {
        queryClient.invalidateQueries(['projects'])
        router.push(`/projects/${data.id}`)
      },
    }
  )
}

export const useDeleteProject = () => {
  const queryClient = useQueryClient()
  const router = useRouter()
  return useMutation(
    async ({ projectId }: { projectId: number }) => {
      const { error } = await supabase
        .from('projects')
        .update({
          archived: true,
        })
        .eq('id', projectId)

      if (error) {
        throw new Error(error.message)
      }
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(['projects'])
        router.push('/dashboard')
      },
    }
  )
}

export const useUpdateProject = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({
      projectId,
      projectToUpdate,
    }: {
      projectId: number
      projectToUpdate: Partial<Project>
    }) => {
      const { data, error } = await supabase
        .from('projects')
        .update(projectToUpdate)
        .eq('id', projectId)
        .select('*')
        .single()

      if (error) {
        throw new Error(error.message)
      }

      return data
    },
    {
      onSuccess: async (_, variables) => {
        const { projectId } = variables
        queryClient.invalidateQueries(['project', projectId])
        queryClient.invalidateQueries(['projects', 'admin'])
        queryClient.invalidateQueries(['projects', 'member'])
        queryClient.invalidateQueries(['projects'])
      },
    }
  )
}
